if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // para V1.20(S-3): 販売終了の時刻を過ぎたあとに古いページ(キャッシュ)が開かれていても、ボタンを止める。
        // 描いた時点で無効のボタン(在庫 0 = SOLD OUT など)は覚えておき、文字を変えない
        this.paraInitiallyDisabled = this.submitButton.hasAttribute('disabled');
        if (this.paraSalesBlocked()) this.paraShowSalesEnded();
      }

      // para V1.20(S-3): 販売状態でカートに入れられない商品か。
      // data-para-sales = ended / before(snippets/para-sales-state の結果・ページを描いた時刻で判定)、
      // data-para-sales-end = 販売終了の時刻(秒)。ブラウザの時刻がそれを過ぎていれば止める
      paraSalesBlocked() {
        if (this.dataset.paraSales) return true;
        const end = Number(this.dataset.paraSalesEnd || 0);
        return end > 0 && Date.now() >= end * 1000;
      }

      paraShowSalesEnded() {
        if (!this.submitButton) return;
        this.submitButton.setAttribute('disabled', 'disabled');
        // 在庫 0 で最初から無効のボタン(SOLD OUT)は文字を変えない(「販売終了」は在庫がある商品だけ)
        if (this.paraInitiallyDisabled) return;
        const label = this.dataset.paraSalesEndedLabel;
        if (label && this.submitButtonText && !this.dataset.paraSales) this.submitButtonText.textContent = label;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.paraSalesBlocked()) {
          this.paraShowSalesEnded();
          return;
        }
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              // para V1.16 修正(FL-13): 入れられなかったとき(1 点物の 2 回目など・422)は reject せず、今のカートで resolve する。
              // reject すると受け手(StandardEvents 側)の派生 promise が未処理の例外としてコンソールに出ていた。エラーは上の CartErrorEvent と注意文で伝わる
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              // para V1.20: 在庫を超えた数を入れようとして一部だけ入ったとき(422)も、ヘッダーのカートの数を今のカートに合わせる
              this.paraRefreshCartCount();

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        // para V1.16(FL-13): カートに入れられなかったとき(1 点物の 2 回目など・422)はこの promise を reject する。
        // 受け手が catch しないと未処理の例外としてコンソールに出るので、ここで受けておく(注意文の表示は handleErrorMessage のまま)
        if (deferred && deferred.promise && typeof deferred.promise.catch === 'function') deferred.promise.catch(() => {});
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      // para V1.20: ヘッダーのカートの数(#cart-icon-bubble)を今のカートで描き直す。
      // 数え方(TIPS を除く)は sections/cart-icon-bubble → snippets/para-cart-count の 1 か所だけ
      // (/cart.js の行には商品タグが無く、TIPS をタグで見分けられないため、Section Rendering で受け取る)。失敗しても何もしない
      paraRefreshCartCount() {
        const bubble = document.getElementById('cart-icon-bubble');
        if (!bubble) return;
        fetch(`${routes.cart_url}?sections=cart-icon-bubble`, { cache: 'no-store' })
          .then((response) => response.json())
          .then((sections) => {
            const html = sections && sections['cart-icon-bubble'];
            if (!html) return;
            const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
            if (source) bubble.innerHTML = source.innerHTML;
          })
          .catch(() => {});
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
